import { useEffect, useState } from "react";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";
import { useSeo, pageSeo, PAGES, graph, crumbs, faqSchema, quickAnswers } from "@/lib/seo";
import { PageHero } from "@/components/common/SectionHeading";
import { FaqList } from "@/components/common/FaqList";
import { CtaBand } from "@/components/common/CtaBand";
import { accent } from "@/lib/ui";

const QuickAnswers = ({ items }) => (
  <section aria-labelledby="faq-quick" data-testid="faq-quick-answers">
    <h2 id="faq-quick" className="mb-6 font-heading text-2xl font-bold text-brand-ink">Quick answers</h2>
    <dl className="grid gap-4 md:grid-cols-2">
      {items.map((q, i) => (
        <div key={q.id} className={`rounded-3xl p-6 ring-1 ring-black/5 ${accent(i).soft}`} data-testid={`faq-quick-${i}`}>
          <dt className="flex items-start gap-2.5 text-base font-bold text-brand-ink md:text-lg"><span className={`mt-2 h-2.5 w-2.5 shrink-0 rounded-full ${accent(i).bar}`} aria-hidden="true" />{q.question}</dt>
          <dd className="mt-2 text-base leading-relaxed text-slate-700">{q.answer}</dd>
        </div>
      ))}
    </dl>
  </section>
);

export default function Faq() {
  const { settings, content } = useSite();
  const [faqs, setFaqs] = useState(null);
  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => setFaqs([]));
  }, []);
  const quick = quickAnswers(settings, content);
  useSeo({ ...pageSeo("/faq"), jsonLd: graph(faqSchema([...quick, ...(faqs || [])]), crumbs(["FAQ", "/faq"])) });
  const clinic = (faqs || []).filter((f) => f.category === "clinic");
  const shop = (faqs || []).filter((f) => f.category === "shop");
  return (
    <>
      <PageHero eyebrow="FAQ" title={PAGES["/faq"].h1} intro={PAGES["/faq"].intro} />
      <div className="container-x space-y-14 py-14 md:py-20">
        <QuickAnswers items={quick} />
        {faqs === null && <p className="text-slate-600">Loading…</p>}
        {clinic.length > 0 && (
          <section aria-labelledby="faq-clinic" data-testid="faq-clinic-section">
            <h2 id="faq-clinic" className="mb-6 font-heading text-2xl font-bold text-brand-ink">Spine care</h2>
            <FaqList faqs={clinic} prefix="faq-clinic" />
          </section>
        )}
        {shop.length > 0 && (
          <section aria-labelledby="faq-shop" data-testid="faq-shop-section">
            <h2 id="faq-shop" className="mb-6 font-heading text-2xl font-bold text-brand-ink">Shop, delivery and returns</h2>
            <FaqList faqs={shop} prefix="faq-shop" />
          </section>
        )}
      </div>
      <CtaBand title="Still have a question?" text="Call or WhatsApp us — we're happy to help." />
    </>
  );
}
